import { format } from 'date-fns';
import TableFilterListDto from 'modules/base/dto/table/TableFilterListDto';
import { getSessionString } from 'modules/base/utils/GenericUtils';
import { Button } from '@material-ui/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import WhatIfNoteTableService from '../service/WhatIfNoteTableService';

function getCreatedLabel(created) {
	if (created == null) return '';
	const value = created instanceof Date ? created : new Date(created);
	return Number.isNaN(value.getTime()) ? '' : format(value, 'yyyy.MM.dd. HH:mm');
}

function NoteItemBox({ data }) {
	const { t } = useTranslation('whatIf');
	const creatorName = data.creator && data.creator.name ? data.creator.name : t('noteUnknownCreator');
	const createdLabel = getCreatedLabel(data.created);
	const hasReadableContent = typeof data.text === 'string' && data.text.trim().length > 0;
	const content = hasReadableContent ? data.text : t('noteNoContent');

	return (
		<div className="flex flex-col gap-2 p-8 pl-0">
			<div className="flex flex-row pl-12">
				<div className="text-stone-400 text-[10px] px-2 font-bold">{creatorName}</div>
				{createdLabel && <div className="text-stone-400 text-[10px] px-2 py-1">{createdLabel}</div>}
			</div>

			<div className="max-w-[75%] px-20 py-16 shadow-md rounded-2xl bg-gradient-to-r from-primary to-secondary">
				<div className="text-sm whitespace-pre-wrap break-words">{content}</div>
			</div>
		</div>
	);
}

function NoteItemList({ noteItemKey, whatIf, updateListRef }) {
	const { t } = useTranslation('whatIf');
	const [sessionString] = useState(() => getSessionString());
	const [dataList, setDataList] = useState([]);
	const [loading, setLoading] = useState(true);
	const [loadFailed, setLoadFailed] = useState(false);
	const itemsListRef = useRef(null);
	const mountedRef = useRef(true);

	const updateList = useCallback(() => {
		if (mountedRef.current) {
			setLoading(true);
			setLoadFailed(false);
		}

		return WhatIfNoteTableService.getList(sessionString, 0, 0)
			.then(resp => {
				if (mountedRef.current) {
					setDataList(Array.isArray(resp.data) ? resp.data : []);
					setLoading(false);
				}
				return resp;
			})
			.catch(() => {
				if (mountedRef.current) {
					setDataList([]);
					setLoading(false);
					setLoadFailed(true);
				}
				return null;
			});
	}, [sessionString]);

	useEffect(() => {
		return () => {
			mountedRef.current = false;
		};
	}, []);

	useEffect(() => {
		updateListRef.current = updateList;
		return () => {
			if (updateListRef.current === updateList) updateListRef.current = null;
		};
	}, [updateList, updateListRef]);

	useEffect(() => {
		let active = true;
		setLoading(true);
		setLoadFailed(false);
		WhatIfNoteTableService.getInstance(
			sessionString,
			true,
			new TableFilterListDto().setup({
				list: [
					{
						propertyName: 'whatIf',
						propertyType: 'Long',
						type: 'EQ',
						value: whatIf
					},
					{
						propertyName: 'noteItemKey',
						propertyType: 'String',
						type: 'EQ',
						value: noteItemKey
					}
				],
				activeCount: 2
			})
		)
			.then(() => (active ? updateList() : null))
			.catch(() => {
				if (active && mountedRef.current) {
					setLoading(false);
					setLoadFailed(true);
				}
			});
		return () => {
			active = false;
		};
	}, [noteItemKey, sessionString, updateList, whatIf]);

	useEffect(() => {
		const frame = window.requestAnimationFrame(() => {
			if (itemsListRef.current) itemsListRef.current.scrollTop = itemsListRef.current.scrollHeight;
		});
		return () => window.cancelAnimationFrame(frame);
	}, [dataList]);

	return (
		<div className="w-full overflow-y-scroll overflow-x-hidden mb-20 flex-1" ref={itemsListRef}>
			<div className="w-full min-h-full p-8 pl-0 flex flex-col items-start gap-2">
				{loading && dataList.length === 0 && <div className="w-full py-16 text-center text-stone-400">{t('notesLoading')}</div>}
				{!loading && loadFailed && (
					<div className="w-full py-16 flex flex-col items-center gap-8 text-stone-400">
						<div>{t('notesLoadFailed')}</div>
						<Button color="primary" onClick={updateList}>
							{t('retry')}
						</Button>
					</div>
				)}
				{!loading && !loadFailed && dataList.length === 0 && <div className="w-full py-16 text-center text-stone-400">{t('notesEmpty')}</div>}
				{dataList.map(data => (
					<NoteItemBox key={data.id} data={data} />
				))}
			</div>
		</div>
	);
}

export default NoteItemList;
